import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const COOLDOWN_SEC = 60;
const STORAGE_KEY = "matpass_verify_resend_at";

function readStoredCooldown() {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return 0;
    const until = Number(raw);
    if (!Number.isFinite(until)) return 0;
    return Math.max(0, Math.ceil((until - Date.now()) / 1000));
  } catch {
    return 0;
  }
}

function storeCooldown(seconds = COOLDOWN_SEC) {
  try {
    sessionStorage.setItem(STORAGE_KEY, String(Date.now() + seconds * 1000));
  } catch {}
}

/**
 * Shared resend + countdown for email verification (60s cooldown).
 */
export function useEmailVerificationResend({ syncStatus = true } = {}) {
  const { user, refreshUser } = useAuth();
  const [cooldown, setCooldown] = useState(() => readStoredCooldown());
  const [sending, setSending] = useState(false);
  const [statusLoading, setStatusLoading] = useState(false);
  const [providerIssues, setProviderIssues] = useState([]);
  const [sandboxMode, setSandboxMode] = useState(false);
  const [sandboxMessage, setSandboxMessage] = useState("");
  const [fromEmail, setFromEmail] = useState("");
  const timerRef = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    timerRef.current = setInterval(() => {
      setCooldown((c) => {
        if (c <= 1) {
          try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [cooldown > 0]); // eslint-disable-line react-hooks/exhaustive-deps

  const syncFromApi = useCallback(async () => {
    if (!user || user.email_verified || !syncStatus) return;
    setStatusLoading(true);
    try {
      const { data } = await api.get("/auth/email-status");
      if (Array.isArray(data?.provider?.issues)) {
        setProviderIssues(data.provider.issues);
      }
      setSandboxMode(Boolean(data?.provider?.sandbox_mode));
      setSandboxMessage(data?.provider?.sandbox_message || "");
      setFromEmail(data?.provider?.from_email || "");
      if (data?.email_verified) {
        await refreshUser?.();
        return data;
      }
      const retry = Number(data?.retry_after) || 0;
      if (retry > 0) {
        setCooldown(retry);
        storeCooldown(retry);
      }
      return data;
    } catch (e) {
      console.warn("[verify-email] status check failed", e);
      return null;
    } finally {
      setStatusLoading(false);
    }
  }, [user, refreshUser, syncStatus]);

  useEffect(() => {
    syncFromApi();
  }, [syncFromApi]);

  const resend = useCallback(async () => {
    if (!user) {
      toast.error("Connectez-vous pour renvoyer l'e-mail");
      return { ok: false };
    }
    if (user.email_verified) {
      toast.info("Votre adresse e-mail est déjà vérifiée");
      return { ok: true, already_verified: true };
    }
    if (cooldown > 0 || sending) {
      toast.info(`Veuillez patienter encore ${cooldown} s`);
      return { ok: false, rate_limited: true };
    }

    setSending(true);
    try {
      const { data } = await api.post("/auth/resend-verification");
      if (data?.already_verified) {
        toast.success("Votre adresse e-mail est déjà vérifiée");
        await refreshUser?.();
        return { ok: true, already_verified: true };
      }
      // Empêcher tout faux succès : n'annoncer l'envoi que si le backend le confirme
      if (data?.email_sent !== true) {
        const msg = data?.email_warning
          || "L'e-mail n'a pas pu être envoyé. Vérifiez la configuration Resend (sandbox).";
        toast.error(msg);
        return { ok: false, email_sent: false };
      }
      const wait = Number(data?.retry_after) || COOLDOWN_SEC;
      setCooldown(wait);
      storeCooldown(wait);
      toast.success(`Un e-mail de vérification a été renvoyé à ${user.email}`);
      return { ok: true, email_sent: true };
    } catch (e) {
      const status = e.response?.status;
      const detail = formatApiError(e.response?.data?.detail);
      console.error("[verify-email] resend failed", { status, detail, error: e });

      if (status === 429) {
        const match = String(detail).match(/(\d+)\s*seconde/);
        const wait = match ? Number(match[1]) : COOLDOWN_SEC;
        setCooldown(wait);
        storeCooldown(wait);
        toast.error(detail || "Délai de renvoi non respecté");
        return { ok: false, rate_limited: true };
      }
      if (status === 503) {
        toast.error(detail || "Erreur du fournisseur d'e-mails");
        return { ok: false, provider_error: true };
      }
      if (!e.response) {
        const unreachable = !e.request
          ? "Impossible de joindre le serveur MatPass (backend arrêté ou inaccessible sur le port 8000)."
          : "Erreur réseau : le backend ne répond pas sur http://localhost:8000. Vérifiez qu'il est démarré.";
        toast.error(unreachable);
        return { ok: false, network_error: true };
      }
      toast.error(detail || "Impossible de renvoyer l'e-mail");
      return { ok: false };
    } finally {
      setSending(false);
    }
  }, [user, cooldown, sending, refreshUser]);

  return {
    cooldown,
    sending,
    statusLoading,
    canResend: !!user && !user.email_verified && cooldown <= 0 && !sending,
    providerIssues,
    sandboxMode,
    sandboxMessage,
    fromEmail,
    resend,
    syncFromApi,
  };
}
